// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/user/components/profile/UserProfileLoadingSkeleton.module.css';
import {PROFILE_POPOUT_GEOMETRY_STYLE} from '@app/features/user/constants/UserProfileSurfaceGeometry';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {motion} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useId, useMemo} from 'react';

const LOADING_PROFILE_DESCRIPTOR = msg({
	message: 'Loading profile',
	comment: 'Accessible label for the user profile skeleton while profile data is loading.',
});

type UserProfileLoadingSkeletonVariant = 'popout' | 'modal';

interface UserProfileLoadingSkeletonProps {
	variant: UserProfileLoadingSkeletonVariant;
	borderColor?: string;
	className?: string;
	[themeAttr: `data-flx${string}`]: string | undefined;
}

const PopoutBannerMask: React.FC<{maskId: string}> = ({maskId}) => (
	<svg className={styles.bannerMask} viewBox="0 0 300 105" preserveAspectRatio="none" aria-hidden>
		<mask id={maskId}>
			<rect fill="white" x="0" y="0" width="300" height="105" />
			<circle fill="black" cx="54" cy="99" r="44" />
		</mask>
		<foreignObject x="0" y="0" width="300" height="105" overflow="visible" mask={`url(#${maskId})`}>
			<div className={styles.bannerFill} />
		</foreignObject>
	</svg>
);

const ModalBannerMask: React.FC<{maskId: string}> = ({maskId}) => (
	<svg className={styles.bannerMask} viewBox="0 0 600 210" preserveAspectRatio="none" aria-hidden>
		<mask id={maskId}>
			<rect fill="white" x="0" y="0" width="600" height="210" />
			<circle fill="black" cx="82" cy="210" r="66" />
		</mask>
		<foreignObject x="0" y="0" width="600" height="210" overflow="visible" mask={`url(#${maskId})`}>
			<div className={styles.bannerFill} />
		</foreignObject>
	</svg>
);

const SkeletonLine: React.FC<{className?: string}> = ({className}) => (
	<div className={clsx(styles.line, className)} aria-hidden="true" />
);

const PopoutSkeletonContent = () => (
	<>
		<div className={styles.content}>
			<SkeletonLine className={styles.nameLine} />
			<SkeletonLine className={styles.usernameLine} />
			<SkeletonLine className={styles.statusLine} />
			<div className={clsx(styles.block, styles.activityBlock)} aria-hidden="true" />
			<div className={styles.section}>
				<SkeletonLine className={styles.sectionTitle} />
				<SkeletonLine className={styles.sectionLineLong} />
				<SkeletonLine className={styles.sectionLineMedium} />
				<SkeletonLine className={styles.sectionLineShort} />
			</div>
			<div className={styles.section}>
				<SkeletonLine className={styles.sectionTitle} />
				<SkeletonLine className={styles.sectionLineMedium} />
				<SkeletonLine className={styles.sectionLineShort} />
			</div>
			<div className={styles.roleRow} aria-hidden="true">
				<div className={styles.pill} />
				<div className={styles.pill} style={{'--profile-skeleton-pill-width': '58px'} as React.CSSProperties} />
				<div className={styles.pill} style={{'--profile-skeleton-pill-width': '72px'} as React.CSSProperties} />
			</div>
			<div className={styles.connectionRow} aria-hidden="true">
				<div className={styles.connectionPill} />
				<div className={styles.connectionPill} />
				<div className={styles.connectionPill} />
			</div>
		</div>
		<div className={styles.footer}>
			<div className={styles.button} aria-hidden="true" />
		</div>
	</>
);

const ModalSkeletonContent = () => (
	<div className={styles.content}>
		<div className={styles.modalUserInfo}>
			<SkeletonLine className={styles.modalNameLine} />
			<SkeletonLine className={styles.modalTagLine} />
			<SkeletonLine className={styles.statusLine} />
		</div>
		<div className={styles.tabs} aria-hidden="true">
			<div className={styles.tab} />
			<div className={styles.tab} />
			<div className={styles.tab} />
		</div>
		<div className={styles.panel}>
			<div className={clsx(styles.block, styles.activityBlock)} aria-hidden="true" />
			<div className={styles.section}>
				<SkeletonLine className={styles.sectionTitle} />
				<SkeletonLine className={styles.sectionLineLong} />
				<SkeletonLine className={styles.sectionLineMedium} />
				<SkeletonLine className={styles.sectionLineShort} />
			</div>
			<div className={styles.mutualRow}>
				<div className={styles.smallAvatar} aria-hidden="true" />
				<div className={styles.mutualText}>
					<SkeletonLine className={styles.sectionLineMedium} />
					<SkeletonLine className={styles.sectionLineShort} />
				</div>
			</div>
			<div className={styles.mutualRow}>
				<div className={styles.smallAvatar} aria-hidden="true" />
				<div className={styles.mutualText}>
					<SkeletonLine className={styles.sectionLineMedium} />
					<SkeletonLine className={styles.sectionLineShort} />
				</div>
			</div>
		</div>
	</div>
);

export const UserProfileLoadingSkeleton: React.FC<UserProfileLoadingSkeletonProps> = observer(
	({variant, borderColor, className, ...props}) => {
		const {i18n} = useLingui();
		const reactId = useId();
		const maskId = `profile_skeleton_${reactId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
		const shouldPulse = !Accessibility.useReducedMotion;
		const style = useMemo(
			() =>
				({
					...(variant === 'popout' ? PROFILE_POPOUT_GEOMETRY_STYLE : {}),
					...(borderColor ? {'--profile-skeleton-border-color': borderColor} : {}),
				}) as React.CSSProperties,
			[borderColor, variant],
		);
		const pulseProps = shouldPulse
			? {
					animate: {opacity: [1, 0.65, 1]},
					transition: {duration: 1.4, ease: 'easeInOut' as const, repeat: Infinity},
				}
			: {};
		return (
			<div
				className={clsx(styles.surface, variant === 'modal' ? styles.modal : styles.popout, className)}
				style={style}
				role="status"
				aria-label={i18n._(LOADING_PROFILE_DESCRIPTOR)}
				data-flx="user.profile.user-profile-loading-skeleton.surface"
				{...props}
			>
				<motion.div
					className={styles.pulse}
					data-flx="user.profile.user-profile-loading-skeleton.pulse"
					{...pulseProps}
				>
					<div className={styles.header} data-flx="user.profile.user-profile-loading-skeleton.header">
						<div className={styles.banner} data-flx="user.profile.user-profile-loading-skeleton.banner">
							{variant === 'modal' ? <ModalBannerMask maskId={maskId} /> : <PopoutBannerMask maskId={maskId} />}
							{variant === 'popout' && (
								<>
									<div className={styles.popoutAvatarSlot} aria-hidden="true" />
									<div className={styles.popoutAvatarStatus} aria-hidden="true" />
								</>
							)}
						</div>
						<div className={styles.headerLower} data-flx="user.profile.user-profile-loading-skeleton.header-lower">
							{variant === 'modal' && <div className={styles.avatar} aria-hidden="true" />}
							{variant === 'modal' && (
								<div className={styles.modalActions} aria-hidden="true">
									<div className={styles.modalActionButton} />
									<div className={styles.modalActionIcon} />
								</div>
							)}
						</div>
					</div>
					{variant === 'modal' ? <ModalSkeletonContent /> : <PopoutSkeletonContent />}
				</motion.div>
			</div>
		);
	},
);
